/**
 * Shared zod schemas for workflow node config primitives.
 *
 * Lives in @chatsdet/types so both `apps/api` (server-side validation +
 * tool schemas) and `packages/canary-cli` (the spec compiler) can import
 * the same source of truth without canary-cli taking a dependency on apps/api.
 *
 * @module workflow-node-schemas
 */

import { z } from 'zod';
import { ValidationOperatorSchema } from './api-sequence.js';

export const FormFieldSchema = z.object({
  name: z
    .string()
    .describe('Unique field identifier (snake_case, e.g., "search_query", "recipient_email")'),
  label: z.string().describe('Human-friendly label (e.g., "Search Query", "Recipient Email")'),
  type: z
    .enum([
      'text',
      'email',
      'password',
      'number',
      'textarea',
      'select',
      'checkbox',
      'multiselect',
      'file',
    ])
    .optional()
    .describe('Input type, defaults to "text"'),
  required: z.boolean().optional().describe('Whether this field is required'),
  value: z.string().describe('The value to use for this field'),
  options: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional()
    .describe('If type is select/multiselect, provide allowed options here'),
});

/**
 * Assertion severity — what happens when the assertion fails:
 * - `fatal`: the test must end now. The run halts immediately at this step.
 * - `fail`: the test may continue. The step is marked failed and the run
 *   finishes marked failed, but later steps still execute.
 * - `warn`: the test should continue. The failure is recorded as a warning
 *   and the run still passes.
 */
export const AssertionSeverityEnum = z.enum(['warn', 'fail', 'fatal']);

export type AssertionSeverity = z.infer<typeof AssertionSeverityEnum>;

export const InlineAssertionSchema = z.object({
  condition: z.string().describe('What to verify after the action'),
  severity: AssertionSeverityEnum.describe(
    'fatal = stop the test immediately on failure, fail = mark the run failed but keep going, warn = log a warning and continue (run still passes)'
  ),
  agentic: z
    .boolean()
    .optional()
    .describe('true if requires visual/complex evaluation that cannot be cached'),
});

/**
 * Subject of a deterministic condition predicate — the left-hand operand.
 *
 * - `variable`: a workflow variable referenced by its input name (resolved from
 *   the run's variable registry as `$var.<name>` at evaluation time).
 * - `acting_as`: the credential identity currently logged in. Compared against a
 *   chosen credential template id; resolved from `currentActingAsTemplateId` on
 *   the run's workflow state.
 */
export const ConditionSubjectSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('variable'),
    name: z.string().describe('Input/variable name, e.g. "count" for $var.count'),
  }),
  z.object({
    kind: z.literal('acting_as'),
  }),
]);

export type ConditionSubject = z.infer<typeof ConditionSubjectSchema>;

/**
 * A single typed comparison that decides a condition node's branch
 * deterministically — no LLM agent. When a condition node has a predicate it is
 * evaluated in the orchestrator; without one it falls back to agentic evaluation
 * of `conditionDescription`. v1 is a single comparison; the shape leaves room to
 * grow into compound AND/OR later.
 */
export const ConditionPredicateSchema = z.object({
  subject: ConditionSubjectSchema,
  operator: ValidationOperatorSchema,
  /**
   * Right-hand operand. For a `variable` subject this is a literal (or `$var`
   * reference); for `acting_as` it is a credential template id. Omitted for the
   * unary operators `is_set` / `is_not_set`.
   */
  value: z.string().optional(),
});

export type ConditionPredicate = z.infer<typeof ConditionPredicateSchema>;

export const NodeTypeEnum = z.enum([
  'login',
  'navigate',
  'action',
  'assertion',
  'seed',
  'setup',
  'wait',
  'condition',
  'api_sequence',
  'end',
  'notification',
  'audio',
  'audio-assertion',
  'visual_checkpoint',
  'sandbox_action',
  'mcp',
  'call_flow',
]);

export type NodeType = z.infer<typeof NodeTypeEnum>;
export type FormField = z.infer<typeof FormFieldSchema>;
export type InlineAssertion = z.infer<typeof InlineAssertionSchema>;
